
import React from 'react';
import { formatCurrency } from '../utils/calculator';
import { InfoTooltip } from '../ui/InfoTooltip';

interface ProjectionTableProps {
  projection: any[];
  formData: any;
}

export function ProjectionTable({ projection, formData }: ProjectionTableProps) {
  const yearsInCare = formData.yearsInCare;
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="bg-grandpal-green/10 p-4 border-b">
          <h4 className="font-semibold text-grandpal-green flex items-center gap-1">
            5-Year Contribution Projection
            <InfoTooltip content="This shows how your contribution will change over the next 5 years, including the impact of the 3-year cap." />
          </h4>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left p-4 font-medium text-grandpal-gray">Year</th>
                <th className="text-right p-4 font-medium text-grandpal-gray">Weekly Contribution</th>
                <th className="text-right p-4 font-medium text-grandpal-gray">From Income</th>
                <th className="text-right p-4 font-medium text-grandpal-gray">From Assets</th>
                {formData.useLoan && (
                  <th className="text-right p-4 font-medium text-grandpal-gray">Deferred Amount</th>
                )}
                {formData.nursingHomeCost > 0 && (
                  <th className="text-right p-4 font-medium text-grandpal-gray">State Support</th>
                )}
                <th className="text-left p-4 font-medium text-grandpal-gray">Notes</th>
              </tr>
            </thead>
            <tbody>
              {projection.map((year) => {
                const actualYear = year.year + yearsInCare;
                const isPastCap = actualYear > 3;
                const propertyDropped = 
                  (formData.ownsPrimaryResidence && actualYear > 3) || 
                  (formData.hasFarmOrBusiness && formData.familySuccessor && actualYear > 3);
                
                return (
                  <tr key={year.year} className="border-t hover:bg-gray-50">
                    <td className="p-4">
                      Year {year.year} <span className="text-sm text-gray-500">(Year {actualYear} in care)</span>
                    </td>
                    <td className="p-4 text-right font-medium">
                      {formatCurrency(formData.useLoan 
                        ? year.totalWeekly - year.deferredAmount 
                        : year.totalWeekly)}
                    </td>
                    <td className="p-4 text-right">{formatCurrency(year.incomeContribution)}</td>
                    <td className="p-4 text-right">{formatCurrency(formData.useLoan 
                      ? year.assetContribution - year.deferredAmount 
                      : year.assetContribution)}
                    </td>
                    {formData.useLoan && (
                      <td className="p-4 text-right text-amber-700">
                        {formatCurrency(year.deferredAmount)}
                      </td>
                    )}
                    {formData.nursingHomeCost > 0 && (
                      <td className="p-4 text-right text-green-700">
                        {year.stateSupport > 0 
                          ? formatCurrency(year.stateSupport) 
                          : "€0.00"}
                      </td>
                    )}
                    <td className="p-4">
                      {isPastCap && propertyDropped && (
                        <span className="text-sm text-green-700">
                          3-year cap applied to {formData.ownsPrimaryResidence ? "home" : ""}
                          {formData.ownsPrimaryResidence && formData.hasFarmOrBusiness && formData.familySuccessor ? " and " : ""}
                          {formData.hasFarmOrBusiness && formData.familySuccessor ? "farm/business" : ""}
                        </span>
                      )}
                      {!isPastCap && actualYear === 3 && (
                        <span className="text-sm text-amber-700">
                          Last year of property assessment
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {formData.useLoan && (
          <div className="p-4 border-t">
            <p className="text-sm font-medium flex items-center gap-1">
              Total Deferred Over 5 Years: {formatCurrency(formData.useLoan ? projection.reduce((sum, year) => sum + (year.deferredAmount * 52), 0) : 0)}
              <InfoTooltip content="This is the approximate amount that will accumulate as a Nursing Home Loan over 5 years." />
            </p>
          </div>
        )}
      </div>
      
      <div className="bg-white p-6 rounded-lg border">
        <h4 className="font-semibold mb-4">Key Points About Your Projection</h4>
        <ul className="space-y-3 text-sm">
          {formData.ownsPrimaryResidence && (
            <li className="flex items-start gap-2">
              <div className="h-5 w-5 rounded-full bg-grandpal-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-grandpal-green">✓</span>
              </div>
              <span>
                Your home contribution stops after Year {Math.max(3 - yearsInCare, 1)} of this projection 
                (Year 3 of total care), reducing your weekly contribution by approximately 
                {formatCurrency(formData.ownsPrimaryResidence ? projection[0].primaryResidenceContribution : 0)}.
              </span>
            </li>
          )}
          {formData.hasFarmOrBusiness && formData.familySuccessor && (
            <li className="flex items-start gap-2">
              <div className="h-5 w-5 rounded-full bg-grandpal-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-grandpal-green">✓</span>
              </div>
              <span>
                Your farm/business contribution stops after Year {Math.max(3 - yearsInCare, 1)} of this projection, 
                reducing your weekly contribution by approximately 
                {formatCurrency(formData.hasFarmOrBusiness && formData.familySuccessor ? projection[0].farmBusinessContribution : 0)}.
              </span>
            </li>
          )}
          {formData.hasOtherProperties && (
            <li className="flex items-start gap-2">
              <div className="h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-amber-700">i</span>
              </div>
              <span>
                Your other properties ({formatCurrency(projection[0].otherPropertiesContribution)}/week) will continue to be assessed 
                throughout your time in care - the 3-year cap does not apply to these assets.
              </span>
            </li>
          )}
          {!formData.hasOtherProperties && !formData.hasFarmOrBusiness && formData.ownsPrimaryResidence && (
            <li className="flex items-start gap-2">
              <div className="h-5 w-5 rounded-full bg-grandpal-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-grandpal-green">✓</span>
              </div>
              <span>
                After Year {Math.max(3 - yearsInCare, 1)} of this projection, your contribution will only be based on your income, 
                as your home will no longer be assessed.
              </span>
            </li>
          )}
          {formData.useLoan && (
            <li className="flex items-start gap-2">
              <div className="h-5 w-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-amber-700">i</span>
              </div>
              <span>
                The Nursing Home Loan will accumulate approximately {formatCurrency(formData.useLoan ? projection.reduce((sum, year) => sum + (year.deferredAmount * 52), 0) : 0)} over the next 5 years, 
                which will be repaid from your estate later.
              </span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
